import { useEffect, useMemo, useState } from "react";

import clsx from "clsx";

import { usePrettyLogEventPage } from "@components/v2/analytics";
import {
  useMetricsExplorerContext,
  useMetricsExplorerStateContext,
} from "@features/metrics-explorer/hooks";
import { type DateRangeChoiceV3 } from "@north-beam/nb-common";
import { FlatSelect, type FlatSelectOption } from "@shared/flat-select";
import { DateRangeCalendarSelect } from "@shared/selects/date-range-calendar-select";

import { dateRangeOptionsForTimeGranularity } from "../utils";

type Props = {
  className?: string;
};

export const DateFilters = ({ className }: Props) => {
  const prettyLogEventPage = usePrettyLogEventPage();
  const {
    attributionModel,
    attributionWindow,
    accountingMode,
    timeGranularity,
    dateRange,
    filterQuery,
    setDateRange,
  } = useMetricsExplorerStateContext();
  const {
    filters: { dateRangeInterval },
  } = useMetricsExplorerContext();

  const pageContext = {
    attributionModel,
    attributionWindow,
    accountingMode,
    timeGranularity,
    filterQuery,
    dateRange,
  };

  const [selected, setSelected] = useState<FlatSelectOption<string>>({
    label: "",
    value: "",
  });

  useEffect(() => {
    if (typeof dateRange !== "string") {
      return setSelected({
        label: "",
        value: "custom",
      });
    }
    setSelected({
      label: "",
      value: dateRange,
    });
  }, [dateRange]);

  const options = useMemo(() => {
    return [
      {
        label: "",
        value: "custom",
        renderOption: ({ isSelected }) => (
          <DateRangeCalendarSelect
            calendarClassName="-ml-1 mt-1"
            value={dateRangeInterval}
            onApply={(v) => {
              prettyLogEventPage({
                domain: "Metrics Explorer",
                action: "onChange",
                additionalActionInfo: "Set Date Range",
                data: {
                  newDateRange: v,
                  pageContext,
                },
              });

              setDateRange(v);
            }}
            buttonComponent={(ref, refProps) => (
              <div
                ref={ref}
                {...refProps}
                className={clsx("h-full flex items-center cursor-pointer", {
                  "text-white": isSelected,
                })}
              >
                Custom
              </div>
            )}
          />
        ),
      },
      ...dateRangeOptionsForTimeGranularity(timeGranularity).map(
        ({ label, value }) => {
          return {
            label,
            value,
            onClick: () => {
              prettyLogEventPage({
                domain: "Metrics Explorer",
                action: "onChange",
                additionalActionInfo: "Set Date Range",
                data: {
                  newDateRange: value,
                  pageContext,
                },
              });

              setDateRange(value as DateRangeChoiceV3);
            },
          };
        },
      ),
    ] as FlatSelectOption<string>[];
  }, [dateRangeInterval, timeGranularity, setDateRange]);

  return (
    <FlatSelect
      className={className}
      options={options}
      setSelected={(v) => {
        setSelected(v);
      }}
      selected={selected}
    />
  );
};
